<template>
  <LoadingWrapper :state="loadingStatus">
    <SideBarLayoutContentHero>
      <template #hero>
        <div class="grid w-full grid-cols-1 gap-2 md:grid-cols-2">
          <div class="bg-surface-container-low rounded-large flex flex-col p-6">
            <div class="mb-4 grow">
              <h1 class="text-display-small">
                {{ survey.title ?? feedbackTopicTitles }}
              </h1>
              <span class="text-body-medium text-on-surface-variant">
                <TimeAgo :date="survey.createdAt" />
                <template v-if="survey.surveyState === SurveyState.finished">
                  <span> · </span>
                  <span>{{ $t('survey.state.finished') }}</span>
                </template>
                <template v-if="survey.title">
                  <span> · </span>
                  <span>{{ feedbackTopicTitles }}</span>
                </template>
              </span>
            </div>
            <div>
              <BaseButtonGroup size="M">
                <BaseComputedButtonGroup
                  class="flex w-full flex-row justify-end"
                  size="M"
                  menu-button-color="tonal"
                  :buttons="[
                    {
                      id: 'selffeedback',
                      condition:
                        selfFeedbackTandem != undefined &&
                        selfFeedbackTandem.state == OpenFeedbackState.InProgress &&
                        survey.surveyState === SurveyState.running,
                      flexibelConfigurations: [
                        { type: 'text', order: 3 },
                        { type: 'icon', order: 4 },
                        { type: 'menu', order: 5 },
                      ],
                      label: 'Selbstfeedback',
                      color: 'filled',
                      shape: 'round',
                      icon: 'undo',
                      callback: () => {
                        router.push({
                          name: 'feedback',
                          params: { surveyId: survey.surveyId, receiverId: member.id },
                        })
                      },
                    },
                    {
                      id: 'finish',
                      condition: survey.surveyState === SurveyState.running && survey.isOwner,
                      flexibelConfigurations: [
                        { type: 'text', order: 1 },
                        { type: 'icon', order: 2 },
                        { type: 'menu', order: 3 },
                      ],
                      label: 'Abschliessen',
                      color: 'tonal',
                      shape: 'square',
                      icon: 'check',
                      callback: openFinishSurveyDialog,
                    },
                    {
                      id: 'edit-perspectives',
                      condition: survey.surveyState === SurveyState.running && survey.isOwner,
                      flexibelConfigurations: [{ type: 'menu', order: 1 }],
                      label: $t('survey.editPerspectives'),
                      color: 'tonal',
                      shape: 'square',
                      icon: '360',
                      callback: openEditPerspectivesDialog,
                    },
                    {
                      id: 'list',
                      flexibelConfigurations: [{ type: 'menu' }],
                      label: $t('survey.showQuestions'),
                      color: 'tonal',
                      shape: 'square',
                      icon: 'list',
                      callback: openShowQuestionsDialog,
                    },
                    {
                      id: 'delete',
                      flexibelConfigurations: [{ type: 'menu', order: 1 }],
                      label: $t('common.delete'),
                      color: 'tonal',
                      shape: 'square',
                      icon: 'delete',
                      destructive: true,
                      callback: openDeleteSurveyDialog,
                    },
                  ]"
                />
              </BaseButtonGroup>
            </div>
          </div>
          <div class="bg-surface-container-low rounded-large flex flex-col p-6">
            <div class="flex flex-col gap-2">
              <div v-if="selfFeedbackTandem" class="flex w-full flex-row items-center gap-4">
                <BaseIcon class="mr-2">{{ iconForSelfFeedback() }}</BaseIcon>
                <div class="flex flex-col">
                  <span class="text-display-small font-medium">{{
                    selfFeedbackTandem.state === OpenFeedbackState.Completed ? 1 : 0
                  }}</span>
                  <span class="text-body-medium text-on-surface-variant -mt-1">{{
                    $t('perspectives.self.default')
                  }}</span>
                </div>
              </div>
              <div
                v-for="group in perspectiveGroups"
                :key="group.perspective.perspectiveKey"
                class="flex w-full flex-row items-center gap-4"
              >
                <BaseIcon class="mr-2">{{ group.perspective.icon }}</BaseIcon>
                <div class="flex flex-col">
                  <span class="text-display-small font-medium"
                    >{{ group.numberOfFeedbacks
                    }}<span v-if="group.numberOfRequiredFeedbacks"
                      >/{{ group.numberOfRequiredFeedbacks }}</span
                    ></span
                  >
                  <span class="text-body-medium text-on-surface-variant -mt-1">{{
                    group.perspective.label
                  }}</span>
                </div>
                <BaseMemberAvatars
                  v-if="sendersForGroup(group.personalTandems).length > 0"
                  class="bg-surface-container-low ml-auto"
                  :members="sendersForGroup(group.personalTandems)"
                />
              </div>
            </div>
          </div>
        </div>
      </template>
      <RunningSurveyView
        v-if="survey.surveyState == SurveyState.running"
        :survey-id="survey.surveyId"
        :number-of-questions="survey.numberOfQuestions"
        :perspective-groups="perspectiveGroups"
        :self-feedback-tandem="selfFeedbackTandem"
        @open-q-r-code-dialog="openLinkQrDialog"
      />
      <CompletedSurveyView v-if="survey.surveyState == SurveyState.finished" />
    </SideBarLayoutContentHero>
    <FninishSurveyDialog
      v-if="survey.isOwner"
      :is-open="isFinishSurveyDialogOpen"
      @close="closeFinishSurveyDialog"
      @fnish-survey="clickFinishSurvey"
    />
    <DeleteSurveyDialog
      v-if="survey.isOwner"
      :is-open="isDeleteSurveyDialogOpen"
      @close="closeDeleteSurveyDialog"
      @delete-survey="clickDeleteSurvey"
    />
    <ExternalFeedbackDialog
      v-if="survey.isOwner && selectedLinkUrl"
      :is-open="isExternalFeedbackDialogOpen"
      :url="selectedLinkUrl"
      @close="closeExternalFeedbackDialog"
      @delete-survey="clickDeleteSurvey"
    />
    <AllQuestionsDialog
      v-if="isShowQuestionsDialogOpen"
      :is-open="isShowQuestionsDialogOpen"
      :feedback-topics="survey.feedbackTopics"
      @close="closeShowQuestionsDialog"
    />
    <EditSurveyPerspectivesDialog
      v-if="survey.isOwner"
      :is-open="isEditPerspectivesDialogOpen"
      :survey="survey"
      @close-dialog="closeEditPerspectivesDialog"
      @survey-updated="reload"
    />
    <QRCodeDownload v-if="selectedLinkUrl" :link="selectedLinkUrl" />
  </LoadingWrapper>
</template>

<script setup lang="ts">
  import LoadingWrapper from '@/core/components/loading/LoadingWrapper.vue'
  import { computed, ref } from 'vue'
  import BaseButtonGroup from '../../../core/components/base/button/BaseButtonGroup.vue'
  import { useSurvey } from '../composables/useSurvey'
  import { useUserStore } from '../../../stores/user.store'
  import { useDialog } from '../../../core/components/base/dialog/use-dialog'
  import { SurveyState } from '../models/survey-state'
  import FninishSurveyDialog from '../components/FninishSurveyDialog.vue'
  import SideBarLayoutContentHero from '../../../core/components/layouts/SideBarLayoutContentHero.vue'
  import BaseComputedButtonGroup from '../../../core/components/base/button/BaseComputedButtonGroup.vue'
  import { useRouter } from 'vue-router'
  import { OpenFeedbackState } from '../../open-feedbacks/composables/useOpenFeedbacks'
  import TimeAgo from '../../../core/components/base/time-ago/TimeAgo.vue'
  import DeleteSurveyDialog from '../components/DeleteSurveyDialog.vue'
  import RunningSurveyView from '../components/RunningSurveyView.vue'
  import CompletedSurveyView from '../components/CompletedSurveyView.vue'
  import ExternalFeedbackDialog from '../components/ExternalFeedbackDialog.vue'
  import QRCodeDownload from '@/core/components/qr-code/QRCodeDownload.vue'
  import BaseIcon from '@/core/components/base/icon/BaseIcon.vue'
  import BaseMemberAvatars from '@/core/components/base/avatar/BaseMemberAvatars.vue'
  import AllQuestionsDialog from '../components/AllQuestionsDialog.vue'
  import EditSurveyPerspectivesDialog from '../components/EditSurveyPerspectivesDialog.vue'
  import type { SurveyTandem } from '../models/survey'
  import type { Member } from '@/core/models/member/member'
  import { SELF_PERSPECTIVE_KEY } from '@/core/models/perspective'
  import { iconForSelfFeedback } from '@/core/models/feedback-perspective.ts'

  const {
    survey,
    loadingStatus,
    finishSurvey,
    deleteSurvey,
    perspectiveGroups,
    selfFeedbackTandem,
    reload,
  } = useSurvey()

  const userStore = useUserStore()
  const router = useRouter()
  const member = userStore.user

  const feedbackTopicTitles = computed(() => {
    return survey.value.feedbackTopics.map((topic) => topic.title).join(', ')
  })

  const sendersForGroup = (tandems: ReadonlyArray<SurveyTandem>): Member[] => {
    return tandems
      .filter(
        (tandem) =>
          tandem.sender &&
          tandem.sender.id != member.id &&
          tandem.perspectiveKey !== SELF_PERSPECTIVE_KEY,
      )
      .map((tandem) => tandem.sender!)
  }

  const selectedLinkUrl = ref<string | undefined>()

  const {
    isOpen: isFinishSurveyDialogOpen,
    openDialog: openFinishSurveyDialog,
    closeDialog: closeFinishSurveyDialog,
  } = useDialog()

  const {
    isOpen: isDeleteSurveyDialogOpen,
    openDialog: openDeleteSurveyDialog,
    closeDialog: closeDeleteSurveyDialog,
  } = useDialog()

  const {
    isOpen: isShowQuestionsDialogOpen,
    openDialog: openShowQuestionsDialog,
    closeDialog: closeShowQuestionsDialog,
  } = useDialog()

  const {
    isOpen: isEditPerspectivesDialogOpen,
    openDialog: openEditPerspectivesDialog,
    closeDialog: closeEditPerspectivesDialog,
  } = useDialog()

  const {
    isOpen: isExternalFeedbackDialogOpen,
    openDialog: openExternalFeedbackDialog,
    closeDialog: closeExternalFeedbackDialog,
  } = useDialog()

  const openLinkQrDialog = (url: string) => {
    selectedLinkUrl.value = url
    openExternalFeedbackDialog()
  }

  const clickFinishSurvey = () => {
    closeFinishSurveyDialog()
    if (survey.value.isOwner) {
      finishSurvey()
    }
  }

  const clickDeleteSurvey = () => {
    closeDeleteSurveyDialog()
    if (survey.value.isOwner) {
      deleteSurvey()
    }
  }
</script>
